import { useQuery } from "@tanstack/react-query";
import { getTreatmentsNoLimit } from "./getTreatmentsNoLimit";
import { ServiceProvided, SessionsDates, Treatment } from "../screens/treatments/treatment-prescription/types";
import {
	activeServicesProvidedFilter,
	countStaffUnavailableSessionsForService,
	getSessionDatesOfServiceProvided,
	getSessionsOfServiceProvided,
	SERVICES_PROVIDED_ENTITY,
} from "../screens/treatments/treatment-prescription/utils";
import { countSessionsOnPeriodicityCurrently, countSessionsOnPreviousPeriod } from "../screens/treatments/treatment-prescription/treatment-prescription-view/services-provided-table/AppointmentFulfillmentTableCell";
import { makeExpectedSessions } from "../screens/treatments/treatment-prescription/treatment-prescription-view/services-provided-table/DoneVsExpectedTableCell";
import { isRoleTechnician } from "../utils/rolesUtils";
import { useUser } from "./useAuth";
import { isDateInCurrentWeek, isDateInCurrentMonth } from "../utils/dateUtils";

const treatmentPrescriptionServicesProvidedFieldsQuery = [
	"id,sessions.status,sessions.id,sessions.start_date,sessions.end_date,sessions.service.id",
    "client.client_id",
	"client.client_id,client.user_id.first_name,client.user_id.last_name",
	"client_owner.id,client_owner.user_id.first_name,client_owner.user_id.last_name,client_owner.user_id.id",
	"theurapists_involved_in_treamtent.id,theurapists_involved_in_treamtent.technicians_id.user_id.id",
	"treatment_prescription.id",
	"treatment_prescription.services_provided.id,treatment_prescription.services_provided.date_begin,treatment_prescription.services_provided.date_end",
	"treatment_prescription.services_provided.service.id,treatment_prescription.services_provided.service.name",
	"treatment_prescription.services_provided.session_quantity,treatment_prescription.services_provided.session_periodicity",
].join(",");

export type TreatmentProvidedAppointment = {
	id: number;
	client_id: string;
	client_name: string;
	client_owner_name: string;
	appointment_fulfillment: Record<
		number,
		{ prescribed_quantity: number; booked_sessions: number; periodicity: string }
	>;
	previous_period_fulfillment: Record<
		number,
		{ prescribed_quantity: number; booked_sessions: number; periodicity: string; started_in_current_period: boolean }
	>;
	next_session_booked: Record<number, { next_booked: string }>;
	last_session_done: Record<number, { last_done: string }>;
	sessions_done_vs_expected: Record<number, { done: number; expected: number }>;
	attendance_rate: number | null;
	therapists_involved_in_treamtent: Array<string>;
	client_owner: string;
};

const additionalFilterByRole = (role: string | undefined) => {
	if (isRoleTechnician(role)) {
		return {
			_and: [
				{ treatment_prescription: { _nnull: true } },
				{
					_or: [
						{ client_owner: { user_id: { _eq: "$CURRENT_USER" } } },
						{ theurapists_involved_in_treamtent: { technicians_id: { user_id: { _eq: "$CURRENT_USER" } } } },
						//{ treatment_prescription: { services_prescribed: { user_created: { _eq: "$CURRENT_USER" } } } }, // To enable prescribed treatments to be seen by prescribers
					],
				},
			],
		};
	}
	return { treatment_prescription: { _nnull: true } };
};


const filterForSingleTreatment = (role: string | undefined, treatmentId: number) => {
	const filter = { _and: [additionalFilterByRole(role), { id: { _eq: treatmentId } }] };
	return filter;
};

type UseTreatmentsProvidedAppointmentsOptions = {
	appointmentFulfillmentOnly?: boolean;
	treatmentId?: number;
};

export const useTreatmentsProvidedAppointments = (options?: UseTreatmentsProvidedAppointmentsOptions) => {
	const { data: user } = useUser();
	const role = user?.role?.id;
	const singleTreatmentId = typeof options?.treatmentId === "number" ? options.treatmentId : undefined;
	const appointmentFulfillmentOnly = options?.appointmentFulfillmentOnly;

	return useQuery({
		queryKey: ["treatments-provided", role, singleTreatmentId ?? "all", appointmentFulfillmentOnly ?? false],
		queryFn: async () => {
			const filter =
				singleTreatmentId === undefined
					? additionalFilterByRole(role)
					: filterForSingleTreatment(role, singleTreatmentId);
			const treatments = await getTreatmentsNoLimit(treatmentPrescriptionServicesProvidedFieldsQuery, filter);
			return getTreatmentsProvidedAppointments(treatments?.items, appointmentFulfillmentOnly);
		},
		staleTime: 1000 * 60 * 3,
	});
};

const getTreatmentsProvidedAppointments = (treatments: Array<Treatment>, appointmentFulfillmentOnly?: boolean) => {
	const treatmentsWithActiveServProvided = treatments.filter((item) =>
		(item.treatment_prescription?.[0] as any)?.[SERVICES_PROVIDED_ENTITY]?.some(activeServicesProvidedFilter)
	);

	const treatmentWithAppointments: Array<TreatmentProvidedAppointment> = treatmentsWithActiveServProvided?.map(
		(treatment) => {
			const treatmentAndActiveServicesProvOnly = removeUnactiveServProvInTreatment(
				treatment,
				activeServicesProvidedFilter
			);

			const clientName = treatment.client.user_id.first_name + " " + treatment.client.user_id.last_name;
			const clientOwnerName = treatment.client_owner
				? treatment.client_owner.user_id.first_name + " " + treatment.client_owner.user_id.last_name
				: "";

			const { appointmentFulfillment, previousPeriodFulfillment } = makeAppointmentFulfillments(treatmentAndActiveServicesProvOnly);

		const attendanceRate = makeAttendanceRate(treatment);

		if (appointmentFulfillmentOnly) {
			return {
				id: treatment.id,
				client_id: treatment.client.client_id,
				client_name: clientName,
				client_owner_name: clientOwnerName,
				appointment_fulfillment: appointmentFulfillment,
				previous_period_fulfillment: previousPeriodFulfillment,
				next_session_booked: {},
				last_session_done: {},
				sessions_done_vs_expected: {},
				attendance_rate: attendanceRate,
				therapists_involved_in_treamtent: treatment?.theurapists_involved_in_treamtent?.map((item) => item.technicians_id.user_id.id) ?? [],
				client_owner: treatment.client_owner?.user_id?.id ?? "",
			};
		}

			const { nextSessionBooked, lastSessionDone } = makeNextAppointmentAndLastDonePerService(
				treatmentAndActiveServicesProvOnly
			);
			const doneVsExpected = makeDoneVsExpectedSessionOverall(treatment);

		return {
			id: treatment.id,
			client_id: treatment.client.client_id,
			client_name: clientName,
			client_owner_name: clientOwnerName,
			appointment_fulfillment: appointmentFulfillment,
			previous_period_fulfillment: previousPeriodFulfillment,
			next_session_booked: nextSessionBooked,
			last_session_done: lastSessionDone,
			sessions_done_vs_expected: doneVsExpected,
			attendance_rate: attendanceRate,
			therapists_involved_in_treamtent: treatment.theurapists_involved_in_treamtent?.map((item) => item.technicians_id.user_id.id) ?? [],
			client_owner: treatment.client_owner?.user_id?.id ?? "",
		};
		}
	);
	return treatmentWithAppointments;
};

const removeUnactiveServProvInTreatment = (
	treatment: Treatment,
	filterFn: (service: ServiceProvided) => boolean
): Treatment => ({
	...treatment,
	treatment_prescription:
		treatment.treatment_prescription?.map((prescription) => ({
			...prescription,
			[SERVICES_PROVIDED_ENTITY]: prescription?.[SERVICES_PROVIDED_ENTITY]?.filter(filterFn) || [],
		})) || [],
});

const makeAppointmentFulfillments = (treatment: Treatment) => {
	const servicesProvided = treatment.treatment_prescription?.[0]?.[SERVICES_PROVIDED_ENTITY];

	const serviceProvidedWithSession = servicesProvided?.map((item: ServiceProvided) => ({
		quantity: item.session_quantity,
		periodicity: item.session_periodicity,
		dateBegin: item.date_begin,
		...getSessionsOfServiceProvided(treatment?.sessions, item),
	}));

	const appointmentFulfillment = Object.assign(
		{},
		...serviceProvidedWithSession.map((item) => ({
			[item.serviceId]: {
				booked_sessions: countSessionsOnPeriodicityCurrently(item.sessions, item.periodicity),
				prescribed_quantity: item.quantity,
				periodicity: item.periodicity,
			},
		}))
	);

	const previousPeriodFulfillment = Object.assign(
		{},
		...serviceProvidedWithSession.map((item) => {
			const dateBegin = new Date(item.dateBegin);
			const startedInCurrentPeriod =
				item.periodicity === "weekly"
					? isDateInCurrentWeek(dateBegin)
					: item.periodicity === "monthly"
						? isDateInCurrentMonth(dateBegin)
						: false;

			return {
				[item.serviceId]: {
					booked_sessions: countSessionsOnPreviousPeriod(item.sessions, item.periodicity),
					prescribed_quantity: item.quantity,
					periodicity: item.periodicity,
					started_in_current_period: startedInCurrentPeriod,
				},
			};
		})
	);

	return { appointmentFulfillment, previousPeriodFulfillment };
};

const makeNextAppointmentAndLastDonePerService = (treatment: Treatment) => {
	if (treatment.sessions.length === 0) return { nextSessionBooked: {}, lastSessionDone: {} };
	const servicesProvided = treatment.treatment_prescription?.[0]?.[SERVICES_PROVIDED_ENTITY];

	const serviceProvidedWithSession = servicesProvided?.map((item: ServiceProvided) =>
		getSessionsOfServiceProvided(treatment?.sessions, item)
	);

	const sessionDates: Record<number, SessionsDates> = Object.assign(
		{},
		...serviceProvidedWithSession.map((item) => ({ [item.serviceId]: getSessionDatesOfServiceProvided(item) }))
	);

	const nextSessionBooked = Object.fromEntries(
		Object.entries(sessionDates).map(([id, item]) => [id, { next_booked: item.latestBookedSession ?? "" }])
	);

	const lastSessionDone = Object.fromEntries(
		Object.entries(sessionDates).map(([id, item]) => [id, { last_done: item.mostRecentDate ?? "" }])
	);
	return { nextSessionBooked, lastSessionDone };
};

const makeAttendanceRate = (treatment: Treatment) => {
	const cancelledCount = treatment.sessions.filter((session) => session.status === "cancelled").length;
	const missedCount = treatment.sessions.filter((session) => session.status === "missed").length;
	const doneCount = treatment.sessions.filter((session) => session.status === "done").length;

	const denominator = cancelledCount + doneCount + missedCount;
	if (denominator === 0) return null;

	const attendanceRate = 100 - ((cancelledCount + missedCount) / denominator) * 100;
	return Math.round(attendanceRate);
};

export const makeDoneVsExpectedSessionOverall = (treatment: Treatment) => {
	const servicesProvided = treatment.treatment_prescription?.[0]?.[SERVICES_PROVIDED_ENTITY];
	
	const serviceProviderAndSessionsDone = servicesProvided.flatMap((servProvided) => {
		const { sessions } = getSessionsOfServiceProvided(treatment?.sessions, servProvided);
		const sessionsDone = sessions.filter((session) => session.status === "done");
		const sessionsDoneOrBooked = sessions.filter(session => ["done", "booked", "confirmed", "awaiting_in_clinic", "in_session" ].includes(session.status))
		const sessionQuantity = servProvided.session_quantity

		const staffUnavailableCount = countStaffUnavailableSessionsForService(treatment.sessions, servProvided);

		return [{ sessionsDone, sessionsDoneOrBooked, servicesProvided: servProvided, sessionQuantity, staffUnavailableCount }];
	});

	const expectedSessionsWithServiceId = serviceProviderAndSessionsDone.map((item) => {
		const serviceId = item.servicesProvided.service.id;
		const expected = makeExpectedSessions(item.servicesProvided, item.sessionsDoneOrBooked, item.staffUnavailableCount);
		return { serviceId, expected: expected.expectedSessions };
	});
	
	
	const expectedSessionsPerService = expectedSessionsWithServiceId.reduce((acc, curr) => {
		if (!acc[curr.serviceId]) acc[curr.serviceId] = 0;
		acc[curr.serviceId] += curr.expected;
		return acc;
	}, {} as Record<number, number>);
	
	const doneSessionsPerService = serviceProviderAndSessionsDone
		.flatMap((item) => item.sessionsDone)
		.reduce((acc, session) => {
			const serviceId = typeof session.service === "object" ? session.service.id : session.service;
			acc[serviceId] = (acc[serviceId] ?? 0) + 1;
			return acc;
		}, {} as Record<number, number>);

	const doneVsExpected = Object.fromEntries(
		Object.entries(expectedSessionsPerService).map(([id, expected]) => {
			const done = doneSessionsPerService[Number(id)] ?? 0;
			return [id, { done, expected }];
		})
	);
	return doneVsExpected;
};